import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Textarea from '../../components/Textarea';
import Loading from '../../components/Loading';
import Modal from '../../components/Modal';
import DomainMultiSelect from '../../components/DomainMultiSelect';
import Badge from '../../components/Badge';
import { useAuth } from '../../context/AuthContext';
import {
  getConferenceDetailsOrganizer,
  updateConference,
  getTracks,
  createTrack,
  updateTrack,
  deleteTrack,
  uploadGeneralChairSignature,
  uploadConferenceRubrics,
  deleteTrackRubric,
  deleteAllConferenceRubrics,
} from '../../utils/api';
import { formatDateIST } from '../../utils/dateFormatter';
import { downloadPdfFile, extractFilename, fetchAsBlobUrl } from '../../utils/pdfHelper';

const ManageConference = () => {
  const { id, conferenceId: confId } = useParams();
  const conferenceId = id || confId; // Handle both route param names
  const navigate = useNavigate();
  const { user } = useAuth();

  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Role
  const [isPrimaryOrganizer, setIsPrimaryOrganizer] = useState(false);
  const [primaryOrganizer, setPrimaryOrganizer] = useState(null);
  const [coOrganizers, setCoOrganizers] = useState([]);

  // Co-organizer editing (primary organizer only)
  const [coOrgInput, setCoOrgInput] = useState('');
  const [coOrgEmails, setCoOrgEmails] = useState([]); // working copy of email strings

  // Track modal state
  const [showTrackModal, setShowTrackModal] = useState(false);
  const [editingTrack, setEditingTrack] = useState(null);
  const [trackForm, setTrackForm] = useState({ name: '', description: '' });
  const [trackSaving, setTrackSaving] = useState(false);

  const [uploadingSignature, setUploadingSignature] = useState(false);

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Rubric management state
  const [rubricFile, setRubricFile] = useState(null);
  const [rubricTrackSelection, setRubricTrackSelection] = useState('ALL'); // 'ALL' | 'SPECIFIC'
  const [selectedRubricTrackIds, setSelectedRubricTrackIds] = useState([]);
  const [uploadingRubric, setUploadingRubric] = useState(false);
  const [rubricError, setRubricError] = useState(null);
  const [rubricSuccess, setRubricSuccess] = useState(null);
  const [deleteRubricConfirm, setDeleteRubricConfirm] = useState(null); // { type: 'track' | 'all', trackId?: string, trackName?: string }
  const [previewRubric, setPreviewRubric] = useState(null); // { url, name }
  const [rubricBlobUrl, setRubricBlobUrl] = useState(null);
  const [rubricBlobLoading, setRubricBlobLoading] = useState(false);

  // Conference form
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    venue: '',
    startDate: '',
    endDate: '',
    submissionDeadline: '',
    reviewDeadline: '',
    generalChairSignaturePath: ''
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [confRes, tracksRes] = await Promise.all([
        getConferenceDetailsOrganizer(conferenceId),
        getTracks(conferenceId)
      ]);

      const confData = confRes.data || confRes;
      setTracks(tracksRes.data || tracksRes || []);

      // Determine role
      const currentUserId = user?._id || user?.id || '';
      const primaryId = confData.primaryOrganizer?._id?.toString() || confData.organizerId?.toString() || '';
      const isPrimary = currentUserId && primaryId && currentUserId.toString() === primaryId;
      setIsPrimaryOrganizer(isPrimary);
      setPrimaryOrganizer(confData.primaryOrganizer || null);

      const coOrgs = confData.coOrganizers || [];
      setCoOrganizers(coOrgs);
      // Seed working email list for editing (primary organizer only)
      setCoOrgEmails(coOrgs.map(c => c.email));

      // Initialize form
      setFormData({
        name: confData.name || '',
        description: confData.description || '',
        venue: confData.venue || '',
        startDate: confData.startDate ? confData.startDate.split('T')[0] : '',
        endDate: confData.endDate ? confData.endDate.split('T')[0] : '',
        submissionDeadline: confData.submissionDeadline ? confData.submissionDeadline.split('T')[0] : '',
        reviewDeadline: confData.reviewDeadline ? confData.reviewDeadline.split('T')[0] : '',
        generalChairSignaturePath: confData.generalChairSignaturePath || ''
      });
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.message || 'Failed to load conference');
    } finally {
      setLoading(false);
    }
  }, [conferenceId, user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Load rubric blob URL when preview opens
  useEffect(() => {
    const isDoc = previewRubric?.name && (
      previewRubric.name.toLowerCase().endsWith('.doc') ||
      previewRubric.name.toLowerCase().endsWith('.docx')
    );

    if (previewRubric?.url && !isDoc) {
      setRubricBlobLoading(true);
      fetchAsBlobUrl(previewRubric.url)
        .then((url) => {
          setRubricBlobUrl(url);
          setRubricBlobLoading(false);
        })
        .catch((err) => {
          console.error('Error loading rubric preview:', err);
          setRubricBlobLoading(false);
        });
    } else {
      setRubricBlobUrl(null);
      setRubricBlobLoading(false);
    }
  }, [previewRubric]);

  useEffect(() => {
    return () => {
      if (rubricBlobUrl) {
        window.URL.revokeObjectURL(rubricBlobUrl);
      }
    };
  }, [rubricBlobUrl]);

  const handleRubricFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setRubricFile(file);
      setRubricError(null);
    }
  };

  const handleToggleTrackCheckbox = (trackId) => {
    setSelectedRubricTrackIds((prev) =>
      prev.includes(trackId)
        ? prev.filter((id) => id !== trackId)
        : [...prev, trackId]
    );
  };

  const handleUploadRubric = async () => {
    if (!rubricFile) {
      setRubricError('Please select a rubric file to upload');
      return;
    }

    let targetIds = 'ALL';
    if (rubricTrackSelection === 'SPECIFIC') {
      if (!selectedRubricTrackIds.length) {
        setRubricError('Please select at least one track');
        return;
      }
      targetIds = selectedRubricTrackIds;
    }

    try {
      setUploadingRubric(true);
      setRubricError(null);
      setRubricSuccess(null);

      const res = await uploadConferenceRubrics(conferenceId, rubricFile, targetIds);
      if (res.data?.tracks) {
        setTracks(res.data.tracks);
      } else {
        await fetchData();
      }

      setRubricSuccess('Evaluation rubric uploaded and assigned successfully!');
      setRubricFile(null);
      const fileInput = document.getElementById('rubric-file-input');
      if (fileInput) fileInput.value = '';
      setTimeout(() => setRubricSuccess(null), 4000);
    } catch (err) {
      console.error('Error uploading rubric:', err);
      setRubricError(err.response?.data?.message || 'Failed to upload rubric document');
    } finally {
      setUploadingRubric(false);
    }
  };

  const handleDeleteTrackRubric = async (trackId) => {
    try {
      setRubricError(null);
      const res = await deleteTrackRubric(conferenceId, trackId);
      if (res.data?.tracks) {
        setTracks(res.data.tracks);
      } else {
        await fetchData();
      }
      setDeleteRubricConfirm(null);
      setRubricSuccess('Rubric removed from track successfully');
      setTimeout(() => setRubricSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting track rubric:', err);
      setRubricError(err.response?.data?.message || 'Failed to remove rubric');
    }
  };

  const handleDeleteAllRubrics = async () => {
    try {
      setRubricError(null);
      const res = await deleteAllConferenceRubrics(conferenceId);
      if (res.data?.tracks) {
        setTracks(res.data.tracks);
      } else {
        await fetchData();
      }
      setDeleteRubricConfirm(null);
      setRubricSuccess('All rubrics removed successfully');
      setTimeout(() => setRubricSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting all rubrics:', err);
      setRubricError(err.response?.data?.message || 'Failed to remove rubrics');
    }
  };

  const handleSignatureUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !conferenceId) return;

    try {
      setUploadingSignature(true);
      setError(null);
      const response = await uploadGeneralChairSignature(conferenceId, file);
      const newPath = response?.data?.path || response?.data?.data?.path || response?.path;

      setFormData((prev) => ({
        ...prev,
        generalChairSignaturePath: newPath || prev.generalChairSignaturePath,
      }));

      setSuccess('Signature uploaded successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error uploading signature:', err);
      setError(err.response?.data?.message || 'Failed to upload signature');
    } finally {
      setUploadingSignature(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setSuccess(null);
  };

  const handleSaveConference = async () => {
    try {
      setSaving(true);
      setError(null);
      const payload = { ...formData };
      // Primary organizer can update co-organizer list
      if (isPrimaryOrganizer) {
        payload.coOrganizerEmails = coOrgEmails;
      }
      await updateConference(conferenceId, payload);
      setSuccess('Conference updated successfully!');
      // Refresh data to reflect resolved co-organizer profiles
      await fetchData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving conference:', err);
      setError(err.response?.data?.message || 'Failed to save conference');
    } finally {
      setSaving(false);
    }
  };

  const addCoOrgEmail = () => {
    const email = coOrgInput.trim().toLowerCase();
    if (!email) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid co-organizer email address');
      return;
    }
    if (coOrgEmails.includes(email)) {
      setError('This email is already in the co-organizer list');
      return;
    }
    setCoOrgEmails([...coOrgEmails, email]);
    setCoOrgInput('');
    setError(null);
  };

  const removeCoOrgEmail = (email) => {
    setCoOrgEmails(coOrgEmails.filter(e => e !== email));
  };

  // Track CRUD
  const openAddTrack = () => {
    setEditingTrack(null);
    setTrackForm({ name: '', description: '' });
    setShowTrackModal(true);
  };

  const openEditTrack = (track) => {
    setEditingTrack(track);
    setTrackForm({ name: track.name, description: track.description || '' });
    setShowTrackModal(true);
  };

  const handleSaveTrack = async () => {
    if (!trackForm.name.trim()) {
      setError('Track name is required');
      return;
    }

    try {
      setTrackSaving(true);
      setError(null);

      if (editingTrack) {
        await updateTrack(editingTrack._id, trackForm);
      } else {
        await createTrack(conferenceId, trackForm);
      }

      // Refresh tracks
      const tracksRes = await getTracks(conferenceId);
      setTracks(tracksRes.data || tracksRes || []);
      setShowTrackModal(false);
      setSuccess(editingTrack ? 'Track updated!' : 'Track added!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving track:', err);
      setError(err.response?.data?.message || 'Failed to save track');
    } finally {
      setTrackSaving(false);
    }
  };

  const handleDeleteTrack = async (trackId) => {
    try {
      setError(null);
      await deleteTrack(trackId);
      setTracks(tracks.filter(t => t._id !== trackId));
      setDeleteConfirm(null);
      setSuccess('Track deleted!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting track:', err);
      setError(err.response?.data?.message || 'Failed to delete track');
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <Loading fullScreen message="Loading conference..." />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <Button variant="outline" size="sm" onClick={() => navigate('/organizer/dashboard')}>
            ← Back to Dashboard
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 sm:mb-8">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Manage Conference</h1>
              {!loading && (
                isPrimaryOrganizer
                  ? <Badge variant="primary" size="sm"> Primary org.</Badge>
                    : <Badge variant="info" size="sm">Co-Organizer</Badge>
              )}
            </div>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Edit conference details and manage tracks</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => navigate(`/organizer/submissions/${conferenceId}`)}>
               Submissions
            </Button>
            <Button variant="secondary" onClick={() => navigate(`/organizer/conferences/${conferenceId}/bids`)}>
               Manage Bids
            </Button>
            <Button variant="secondary" onClick={() => navigate(`/organizer/conferences/${conferenceId}/assignments`)}>
               Assignments
            </Button>
            <Button variant="secondary" onClick={() => navigate(`/organizer/conferences/${conferenceId}/reviewer-requests`)}>
               Reviewer Requests
            </Button>
            <Button variant="secondary" onClick={() => navigate(`/organizer/conferences/${conferenceId}/authors`)}>
               Certificates
            </Button>
          </div>
        </div>



        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-800">{success}</p>
          </div>
        )}

        {/* Conference Details Form */}
        <Card className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Conference Details</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Conference Name
              </label>
              <Input
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Enter conference name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <Textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Enter conference description"
                rows={4}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Venue
              </label>
              <Input
                value={formData.venue}
                onChange={(e) => handleInputChange('venue', e.target.value)}
                placeholder="Enter venue location"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <Input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleInputChange('startDate', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <Input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => handleInputChange('endDate', e.target.value)}
                  min={formData.startDate}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Submission Deadline
                </label>
                <Input
                  type="date"
                  value={formData.submissionDeadline}
                  onChange={(e) => handleInputChange('submissionDeadline', e.target.value)}
                  max={formData.startDate}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Review Deadline
                </label>
                <Input
                  type="date"
                  value={formData.reviewDeadline}
                  onChange={(e) => handleInputChange('reviewDeadline', e.target.value)}
                  min={formData.submissionDeadline}
                  max={formData.startDate}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                General Chair Signature
              </label>
              <div className="space-y-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleSignatureUpload}
                  disabled={uploadingSignature}
                  className="block w-full text-sm text-gray-700
                             file:mr-4 file:py-2 file:px-4
                             file:rounded-md file:border-0
                             file:text-sm file:font-semibold
                             file:bg-primary-50 file:text-primary-700
                             hover:file:bg-primary-100"
                />
                {uploadingSignature && (
                  <p className="text-xs text-gray-500">Uploading signature...</p>
                )}
                {formData.generalChairSignaturePath && (
                  <p className="text-xs text-emerald-700">
                    Current signature: {formData.generalChairSignaturePath}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  This image will be rendered above the signature line on generated certificates.
                </p>
              </div>
            </div>

            {/* Co-Organizers Section */}
            <div className="border-t pt-4">
              <h3 className="text-base font-semibold text-gray-900 mb-3">Co-Organizers</h3>

              {isPrimaryOrganizer ? (
                <>
                  <p className="text-sm text-gray-500 mb-3">
                    Add or remove organizers who help manage this conference.
                    Changes are saved when you click &quot;Save Changes&quot;.
                  </p>
                  {/* Add email row */}
                  <div className="flex gap-2 mb-3">
                    <input
                      type="email"
                      value={coOrgInput}
                      onChange={(e) => { setCoOrgInput(e.target.value); setError(null); }}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCoOrgEmail())}
                      placeholder="co-organizer@example.com"
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={addCoOrgEmail}
                      className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                    >
                      Add
                    </button>
                  </div>

                  {/* Pending email list */}
                  {coOrgEmails.length > 0 ? (
                    <div className="space-y-2">
                      {coOrgEmails.map((email) => {
                        const profile = coOrganizers.find(c => c.email === email);
                        return (
                          <div key={email} className="flex items-center justify-between bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2">
                            <div className="flex items-center gap-2">
                              <span className="text-indigo-600">👤</span>
                              <div>
                                {profile?.name && <p className="text-sm font-medium text-gray-900">{profile.name}</p>}
                                <p className="text-sm text-gray-600">{email}</p>
                              </div>
                              <Badge variant="info" size="sm">Co-Organizer</Badge>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeCoOrgEmail(email)}
                              className="text-red-500 hover:text-red-700 text-sm font-medium"
                            >
                              Remove
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 italic">No co-organizers assigned yet.</p>
                  )}
                </>
              ) : (
                // Read-only view for co-organizers
                <div className="space-y-2">
                  {primaryOrganizer && (
                    <div className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2">
                      <span className="text-purple-600">👑</span>
                      <div>
                        {primaryOrganizer.name && <p className="text-sm font-medium text-gray-900">{primaryOrganizer.name}</p>}
                        <p className="text-sm text-gray-600">{primaryOrganizer.email}</p>
                      </div>
                      <Badge variant="primary" size="sm">Primary Organizer</Badge>
                    </div>
                  )}
                  {coOrganizers.filter(c => c.email !== (user?.email || '')).map(c => (
                    <div key={c._id} className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2">
                      <span className="text-indigo-600">👤</span>
                      <div>
                        {c.name && <p className="text-sm font-medium text-gray-900">{c.name}</p>}
                        <p className="text-sm text-gray-600">{c.email}</p>
                      </div>
                      <Badge variant="info" size="sm">Co-Organizer</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="pt-4">
              <Button
                onClick={handleSaveConference}
                disabled={saving}
                className={saving ? 'opacity-50 cursor-not-allowed' : ''}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
              {error && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800">{error}</p>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Track Management */}
        <Card>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">Tracks</h2>
            <Button onClick={openAddTrack}>
              + Add Track
            </Button>
          </div>

          {tracks.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">📂</div>
              <p className="text-gray-600 mb-4">No tracks created yet</p>
              <Button onClick={openAddTrack}>
                Create First Track
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {tracks.map((track) => (
                <div
                  key={track._id}
                  className="flex justify-between items-center p-4 bg-gray-50 rounded-lg border"
                >
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{track.name}</h3>
                    {track.description && (
                      <p className="text-sm text-gray-600 mt-1">{track.description}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditTrack(track)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-600 hover:bg-red-50"
                      onClick={() => setDeleteConfirm(track)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Evaluation Rubrics Management */}
        <Card>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-6">
            <div>
              <div className="flex items-center gap-2">
                <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900">Review Rubrics Documents</h2>
                  <p className="text-sm text-gray-600">
                    Provide evaluation criteria and rubrics for reviewers (PDF, DOC, DOCX up to 10MB).
                  </p>
                </div>
              </div>
            </div>
            {tracks.some(t => t.rubricUrl) && (
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 border-red-300 hover:bg-red-50 inline-flex items-center gap-1.5"
                onClick={() => setDeleteRubricConfirm({ type: 'all' })}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Remove All Rubrics
              </Button>
            )}
          </div>

          {/* Feedback alerts */}
          {rubricSuccess && (
            <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-3">
              <svg className="w-5 h-5 text-emerald-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <p className="text-sm font-medium text-emerald-800">{rubricSuccess}</p>
            </div>
          )}

          {rubricError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
              <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm font-medium text-red-800">{rubricError}</p>
            </div>
          )}

          {/* Upload and track assignment form */}
          <div className="p-5 bg-gray-50 border border-gray-200 rounded-xl mb-6">
            <h3 className="text-sm font-bold uppercase tracking-wider text-gray-700 mb-4">
              Upload &amp; Assign Rubric
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* File picker */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rubric Document (PDF, DOC, DOCX - max 10MB)
                </label>
                <div className="relative border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-indigo-400 transition-colors bg-white">
                  <input
                    id="rubric-file-input"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleRubricFileChange}
                    disabled={uploadingRubric}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <div className="flex flex-col items-center justify-center text-center">
                    <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    {rubricFile ? (
                      <div>
                        <p className="text-sm font-semibold text-indigo-600">{rubricFile.name}</p>
                        <p className="text-xs text-gray-500">{(rubricFile.size / (1024 * 1024)).toFixed(2)} MB</p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm text-gray-600 font-medium">Click to choose rubric file or drag and drop</p>
                        <p className="text-xs text-gray-400 mt-1">PDF format allows in-browser preview for reviewers</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Target tracks selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assign To Tracks
                </label>

                <div className="space-y-3">
                  <div className="flex items-center gap-4">
                    <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-gray-800">
                      <input
                        type="radio"
                        name="rubricTrackSelection"
                        value="ALL"
                        checked={rubricTrackSelection === 'ALL'}
                        onChange={() => setRubricTrackSelection('ALL')}
                        className="text-indigo-600 focus:ring-indigo-500"
                      />
                      <span>All Tracks (Conference-wide)</span>
                    </label>

                    <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-gray-800">
                      <input
                        type="radio"
                        name="rubricTrackSelection"
                        value="SPECIFIC"
                        checked={rubricTrackSelection === 'SPECIFIC'}
                        onChange={() => setRubricTrackSelection('SPECIFIC')}
                        className="text-indigo-600 focus:ring-indigo-500"
                      />
                      <span>Select Specific Tracks</span>
                    </label>
                  </div>

                  {rubricTrackSelection === 'SPECIFIC' && (
                    <div className="p-3 bg-white border border-gray-200 rounded-lg max-h-48 overflow-y-auto space-y-2">
                      {tracks.length === 0 ? (
                        <p className="text-xs text-gray-500 italic">No tracks created yet</p>
                      ) : (
                        tracks.map((track) => (
                          <label
                            key={track._id}
                            className="flex items-center gap-2.5 p-1.5 hover:bg-gray-50 rounded cursor-pointer text-sm text-gray-700"
                          >
                            <input
                              type="checkbox"
                              checked={selectedRubricTrackIds.includes(track._id)}
                              onChange={() => handleToggleTrackCheckbox(track._id)}
                              className="rounded text-indigo-600 focus:ring-indigo-500"
                            />
                            <span className="font-medium text-gray-800">{track.name}</span>
                            {track.rubricOriginalName && (
                              <span className="text-xs text-indigo-600 ml-auto truncate max-w-[150px]">
                                (Has: {track.rubricOriginalName})
                              </span>
                            )}
                          </label>
                        ))
                      )}
                    </div>
                  )}

                  <div className="pt-1">
                    <Button
                      onClick={handleUploadRubric}
                      disabled={uploadingRubric || !rubricFile}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2"
                    >
                      {uploadingRubric ? (
                        <>
                          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Uploading Rubric...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                          </svg>
                          Upload &amp; Assign Rubric
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Current track rubrics list */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-gray-700 mb-3">
              Track Rubrics Status
            </h3>

            {tracks.length === 0 ? (
              <p className="text-sm text-gray-500 py-4 text-center">No tracks exist for this conference.</p>
            ) : (
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Track Name</th>
                      <th className="px-4 py-3 text-left font-semibold">Assigned Rubric</th>
                      <th className="px-4 py-3 text-left font-semibold">Uploaded Date</th>
                      <th className="px-4 py-3 text-right font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {tracks.map((track) => {
                      const hasRubric = Boolean(track.rubricUrl);
                      return (
                        <tr key={track._id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                            {track.name}
                          </td>
                          <td className="px-4 py-3">
                            {hasRubric ? (
                              <div className="flex items-center gap-2 text-indigo-700">
                                <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <span className="font-medium truncate max-w-xs" title={track.rubricOriginalName}>
                                  {track.rubricOriginalName || 'Rubric Document'}
                                </span>
                              </div>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                                No Rubric Assigned
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                            {track.rubricUploadedAt ? formatDateIST(track.rubricUploadedAt) : '-'}
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            {hasRubric ? (
                              <div className="inline-flex items-center gap-2">
                                <button
                                  type="button"
                                  title="View Rubric"
                                  onClick={() => setPreviewRubric({ url: track.rubricUrl, name: track.rubricOriginalName || `${track.name} Rubric` })}
                                  className="p-1.5 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  title="Download Rubric"
                                  onClick={() => downloadPdfFile(track.rubricUrl, extractFilename(track.rubricUrl, track.rubricOriginalName || 'rubric'))}
                                  className="p-1.5 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  title="Remove Rubric"
                                  onClick={() => setDeleteRubricConfirm({ type: 'track', trackId: track._id, trackName: track.name })}
                                  className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => {
                                  setRubricTrackSelection('SPECIFIC');
                                  setSelectedRubricTrackIds([track._id]);
                                  const fileInput = document.getElementById('rubric-file-input');
                                  if (fileInput) fileInput.focus();
                                }}
                                className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                              >
                                + Assign Rubric
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Track Modal */}
      <Modal
        isOpen={showTrackModal}
        onClose={() => setShowTrackModal(false)}
        title={editingTrack ? 'Edit Track' : 'Add Track'}
      >
        <div className="space-y-4">
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Track Name <span className="text-red-500">*</span>
            </label>
            <DomainMultiSelect
              value={trackForm.name ? [trackForm.name] : []}
              onChange={(domains) => setTrackForm(prev => ({ ...prev, name: domains[0] || '' }))}
              placeholder="Select a track domain..."
              singleSelect={true}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <Textarea
              value={trackForm.description}
              onChange={(e) => setTrackForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe what this track covers"
              rows={3}
            />
          </div>
          <div className="flex gap-3 pt-2">
            <Button
              onClick={handleSaveTrack}
              fullWidth
              disabled={trackSaving}
            >
              {trackSaving ? 'Saving...' : (editingTrack ? 'Update Track' : 'Add Track')}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowTrackModal(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Track Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Delete Track?</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete &quot;{deleteConfirm.name}&quot;? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <Button
                className="bg-red-600 hover:bg-red-700"
                onClick={() => handleDeleteTrack(deleteConfirm._id)}
              >
                Delete
              </Button>
              <Button
                variant="outline"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Rubric Confirmation Modal */}
      {deleteRubricConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {deleteRubricConfirm.type === 'all' ? 'Remove All Rubrics?' : `Remove Rubric from ${deleteRubricConfirm.trackName}?`}
            </h3>
            <p className="text-gray-600 mb-6">
              {deleteRubricConfirm.type === 'all'
                ? 'This will remove rubrics across all tracks and permanently delete unused files from Cloudinary. Reviewers will no longer see rubrics for these tracks.'
                : `Are you sure you want to remove the evaluation rubric for "${deleteRubricConfirm.trackName}"? The file will be removed from Cloudinary if not used by other tracks.`}
            </p>
            <div className="flex gap-3">
              <Button
                className="bg-red-600 hover:bg-red-700"
                onClick={() => {
                  if (deleteRubricConfirm.type === 'all') {
                    handleDeleteAllRubrics();
                  } else {
                    handleDeleteTrackRubric(deleteRubricConfirm.trackId);
                  }
                }}
              >
                Remove
              </Button>
              <Button
                variant="outline"
                onClick={() => setDeleteRubricConfirm(null)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Rubric Preview Modal */}
      {previewRubric && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="text-lg font-bold text-gray-900 truncate max-w-xl">
                  {previewRubric.name || 'Rubric Document Preview'}
                </h3>
              </div>
              <button
                onClick={() => setPreviewRubric(null)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div style={{ height: '65vh' }} className="bg-gray-100 flex items-center justify-center">
              {rubricBlobLoading ? (
                <div className="text-center">
                  <svg className="animate-spin h-10 w-10 text-indigo-600 mx-auto mb-3" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <p className="text-sm text-gray-600 font-medium">Loading Rubric PDF...</p>
                </div>
              ) : rubricBlobUrl ? (
                <iframe
                  src={rubricBlobUrl}
                  className="w-full h-full border-0"
                  title="Rubric Preview"
                />
              ) : (
                <div className="text-center p-8 text-gray-600">
                  <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="font-medium text-gray-800">Preview not available for this file type.</p>
                  <p className="text-xs text-gray-500 mt-1 mb-4">You can download and view the rubric document locally.</p>
                  <Button
                    size="sm"
                    onClick={() => downloadPdfFile(previewRubric.url, extractFilename(previewRubric.url, previewRubric.name))}
                    className="inline-flex items-center gap-1.5"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download File
                  </Button>
                </div>
              )}
            </div>

            <div className="px-6 py-3 border-t bg-gray-50 flex justify-between items-center">
              <button
                onClick={() => downloadPdfFile(previewRubric.url, extractFilename(previewRubric.url, previewRubric.name))}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 border border-indigo-200 hover:border-indigo-400 rounded-lg bg-white transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download Rubric
              </button>
              <Button variant="outline" size="sm" onClick={() => setPreviewRubric(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ManageConference;
